import React, { Component } from 'react';
import moment from 'moment';
import { parse } from 'date-fns';
import styled from '@emotion/styled';
import SessionStore from 'stores/shared/session';
import ApiService from 'services/api';
import { findLocale } from 'services/translation';
import {
  FormattedMessage,
  FormattedDate,
  injectIntl,
  defineMessages,
} from 'react-intl';

import { Theme } from 'ui_system/theme';
import ModalActionCreator from 'action_creators/modal';
import { localeDateFormats } from 'views/plugins/tours/locales/localMomentDateFormats';

import Dimmer from 'views/shared/ui/dimmer';
import { GuestReschedule } from 'views/guest_reschedule';
import { finalFormattedRefundAmount, getGuestsByTicketType } from './helpers';
import BookingSummary from './BookingSummary';
import ContactModal from './ContactModal';

import './index.scss';
import BookingCancelModal from './BookingCancelModal';

const unitMessages = defineMessages({
  guest: { defaultMessage: 'guest' },
  guests: { defaultMessage: 'guests' },
  adult: { defaultMessage: 'adult' },
  adults: { defaultMessage: 'adults' },
  child: { defaultMessage: 'child' },
  children: { defaultMessage: 'children' },
  senior: { defaultMessage: 'senior' },
  seniors: { defaultMessage: 'seniors' },
  person: { defaultMessage: 'person' },
  people: { defaultMessage: 'people' },
  youth: { defaultMessage: 'youth' },
  infant: { defaultMessage: 'infant' },
  infants: { defaultMessage: 'infants' },
  student: { defaultMessage: 'student' },
  students: { defaultMessage: 'students' },
});

const translateUnitDescriptor = (intl, value) => {
  const key = value?.toLowerCase();
  if (key && unitMessages[key]) {
    return intl.formatMessage(unitMessages[key]);
  }
  return value;
};

const CancellationSummaryContainer = styled.div`
  margin-top: 32px;
`;

const CancellationRefundSection = styled.p`
  margin-top: 32px;
`;

const BookingActionButton = styled.button`
  color: ${(props) => props.buttonColor};
  border-color: ${(props) => props.buttonColor};
  background-color: transparent;

  &:hover {
    color: #fff;
    background-color: ${(props) => props.buttonColor};
    border-color: ${(props) => props.buttonColor};
  }
`;

class ModifyBooking extends Component {
  constructor(props) {
    super(props);

    const { booking } = this.props;

    this.state = {
      bookingProps: this.bookingProps(booking),
      showConfirmModal: false,
      loading: false,
      cancellationPreview: {},
      cancellationPreviewLoading: true,
      selectedRefund: '',
      cancelLoading: false,
      cancelError: false,
      isRescheduling: false,
    };
    this.rescheduleStepRef = { current: 'form' };
  }

  componentDidMount = () => {
    const { booking } = this.props;
    const { booking_id_hash } = booking;

    const cancellationPreviewData = ApiService.fetch({
      url: `/api/bookings/${booking_id_hash}/cancellation_preview.json`,
    });

    cancellationPreviewData
      .then((response) => {
        const cancellationPreview = response.data.attributes;
        const { accountRefundType } = cancellationPreview;
        this.setState({
          cancellationPreview,
          selectedRefund: accountRefundType,
          cancellationPreviewLoading: false,
        });
      })
      .bind(this);
  };

  bookingProps = (booking) => {
    // Guest vs guests, etc.
    const numberOfGuests = booking.number_of_tourists;
    let guestsDescriptor = translateUnitDescriptor(
      this.props.intl,
      booking.root_unit_trans.noun_singular
    );
    if (numberOfGuests > 1) {
      guestsDescriptor = translateUnitDescriptor(
        this.props.intl,
        booking.root_unit_trans.noun_plural
      );
    }

    // Date, time formatting
    const { date, time } = booking.booking_slot;
    const dateTimeObject = moment(
      `${date.month_name} ${date.day}, ${date.year} ${time}`
    ).locale(findLocale());
    const timeFormatString = this.props.booking?.tour?.iso_time
      ? 'HH:mm'
      : 'hh:mm A';

    const formattedTime = dateTimeObject.format(timeFormatString);

    const dateFormat = localeDateFormats(findLocale());
    const formattedDate = dateTimeObject.format(dateFormat);

    const bookingUnits = booking.pricing_configuration?.units;
    const guestsByTicketType = getGuestsByTicketType(bookingUnits);
    const isCapacityByTicketType = booking.is_ticket_per_capacity_type;

    return {
      id: booking.id,
      id_hash: booking.id_hash,
      guestName: booking.tourist.name,
      guestEmail: booking.tourist.email,
      price: booking.price,
      numberOfGuests,
      guestsDescriptor,
      guestsByTicketType,
      isCapacityByTicketType,
      date,
      formattedDate,
      time,
      formattedTime,
      bookingSlot: booking.booking_slot,
      tour: booking.tour,
      guest_availability_dates: booking.guest_availability_dates,
      tour_name_translation: booking.tour_name_translation,
      cancellation_policy: booking.cancellation_policy,
      hasResource: booking.has_resource,
      resource: booking.resource,
      cardLast4: booking.card_last4,
    };
  };

  bookingReschedulable = () => {
    const { booking } = this.props;
    if (!booking.in_guest_reschedule_window) return false;
    if (!booking.future_guest_availability) return false;
    if (!booking.tour.guest_reschedulable) return false;
    if (booking.current_state === 'cancelled') return false;
    if (this.isTicketCapacityJobInProgress()) return false;

    return true;
  };

  bookingCancellable = () => {
    const {
      booking,
      booking: {
        in_guest_cancelable_window: inGuestCancelableWindow,
        current_state: currentState,
      },
    } = this.props;
    const { guest_cancelable: guestCancelable } = booking.tour;

    const { cancellationPreview } = this.state;
    const { paymentType } = cancellationPreview;

    if (!inGuestCancelableWindow) return false;
    if (!guestCancelable) return false;
    if (paymentType === 'cash') return false;
    if (currentState !== 'confirmed') return false;
    return true;
  };

  isTicketCapacityJobInProgress = () => {
    const { ticket_capacity_job_in_progress: ticketCapacityJobInProgress } =
      this.props.booking;
    return ticketCapacityJobInProgress;
  };

  renderTicketCapacityJobInProgressWarning = () => {
    if (!this.isTicketCapacityJobInProgress()) {
      return null;
    }
    return (
      <div className="ModifyBooking-TicketCapacityJobWarning">
        <div className="ModifyBooking-TicketCapacityJobWarning-icon">
          <i className="af-lock" />
        </div>
        <div className="ModifyBooking-TicketCapacityJobWarning-message">
          <FormattedMessage
            id="PREVIOUS_CAPACITY_EDITS_ARE_UPDATING_REFRESH_MESSAGE"
            defaultMessage="Previous capacity edits are updating. To make additional edits, please refresh this page."
          />
        </div>
      </div>
    );
  };

  openContactModal = (e) => {
    let modalSettings = {
      content: this.renderContactModal(),
      triggeringElement: e.currentTarget,
    };

    // Mobile options
    if (window.innerWidth <= Theme.mobile.phoneWidth - 1) {
      modalSettings = {
        ...modalSettings,
        className: 'full-screen',
        showIcon: false,
        settings: {
          transition: 'slide up',
        },
      };
    }

    ModalActionCreator.show(modalSettings);
  };

  toggleCancelModal = (visibility) => {
    this.setState({
      showConfirmModal: visibility,
      cancelError: false,
    });
  };

  cancelBooking = () => {
    this.setState({
      cancelLoading: true,
    });

    const {
      booking: { booking_id_hash: bookingIdHash },
    } = this.props;

    const params = { id: bookingIdHash };

    const cancelBooking = ApiService.fetch({
      url: `/api/bookings/${params.id}/guest_cancellation`,
      method: 'PUT',
      params: { ...params, ...SessionStore._getCSRFTokenObject() },
    });

    cancelBooking
      .then(() => {
        this.setState({
          loading: true,
          showConfirmModal: false,
        });
        window.location.reload();
      })
      .catch(() => {
        this.setState({
          cancelLoading: false,
          cancelError: true,
        });
      })
      .finally(() => {
        this.setState({
          cancelLoading: false,
        });
      })
      .bind(this);
  };

  renderDesktopHeader = () => {
    const { color, logo, name } = this.props.header_data;
    const bgStyle = { backgroundColor: color };

    let content = name;
    if (logo) {
      content = <img className="headerLogo" src={logo} alt={name} />;
    }

    // Shown on both desktop aand mobile
    return (
      <div className="desktopHeader" style={bgStyle}>
        {content}
      </div>
    );
  };

  renderMobileHeader = () => {
    return (
      <div className="mobileHeader">
        <FormattedMessage id="MODIFY_BOOKING" defaultMessage="Modify booking" />
      </div>
    );
  };

  renderMobileCover = () => {
    const { tour, tour_name_translation: tourNameTranslation } =
      this.props.booking;
    return (
      <div
        className="mobileCover"
        style={{ backgroundImage: `url('${tour.cover_picture}')` }}
      >
        <div className="coverDetails">
          <div className="tourName">{tourNameTranslation}</div>
          <div className="businessName">{tour.business_name}</div>
        </div>
      </div>
    );
  };

  renderContactModal = () => {
    const { booking } = this.props;
    const { tour, tourist } = booking;

    return (
      <ContactModal
        authenticityToken={this.props.authenticity_token}
        buttonColor={this.props.header_data.button_background_color}
        bookingId={booking.id}
        guestEmail={tourist.email}
        guestId={tourist.id_hash}
        businessName={tour.business_name}
        originLocation="modify_booking"
      />
    );
  };

  renderCancelModal = () => {
    const {
      showConfirmModal,
      cancelLoading,
      cancelError,
      loading,
      selectedRefund,
      cancellationPreview,
    } = this.state;
    const { booking } = this.props;
    const { is_free: isFree } = booking.tour;

    return (
      <>
        <Dimmer loader={loading} active={loading} fixed />
        <BookingCancelModal
          open={showConfirmModal}
          close={() => this.toggleCancelModal(false)}
          label={this.props.intl.formatMessage({
            defaultMessage: 'Cancel booking',
          })}
          text={this.props.intl.formatMessage({
            defaultMessage: 'Are you sure you want to cancel your booking',
          })}
          refundAmount={finalFormattedRefundAmount(
            selectedRefund,
            cancellationPreview
          )}
          cancellationPolicy={booking.cancellation_policy?.description}
          isFree={isFree}
          onKeepBooking={() => this.toggleCancelModal(false)}
          onCancelBooking={this.cancelBooking}
          cancelLoading={cancelLoading}
          cancelError={cancelError}
        />
      </>
    );
  };

  onCancelReschedule = (options) => {
    this.setState({ isRescheduling: false });
    if (
      options?.afterSuccess ||
      this.rescheduleStepRef?.current === 'booking-updated'
    ) {
      window.location.reload();
    }
  };

  renderLeftPanel = () => {
    const { selectedRefund, cancellationPreview } = this.state;
    const booking = this.state.bookingProps;
    const { booking: propsBooking } = this.props;
    const { brand_color, button_background_color } = this.props.header_data;
    const { email } = propsBooking.tourist;
    const linkStyle = { color: brand_color };
    const { is_free: isFree } = booking.tour;
    const bookingCancelled = propsBooking.current_state === 'cancelled';

    let ticketLink = null;
    if (
      propsBooking.tour.ticketed &&
      propsBooking.current_state !== 'cancelled'
    ) {
      ticketLink = (
        <div className="ticketLink">
          <a
            className="modifyBookingLink"
            href={propsBooking.link_to_ticket}
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
          >
            {propsBooking.has_multiple_tickets ? (
              <span>
                <FormattedMessage
                  id="VIEW_TICKETS"
                  defaultMessage="View tickets"
                />{' '}
                &rarr;
              </span>
            ) : (
              <span>
                <FormattedMessage
                  id="VIEW_TICKET"
                  defaultMessage="View ticket"
                />{' '}
                &rarr;
              </span>
            )}
          </a>
        </div>
      );
    }

    let pageHeader = this.props.intl.formatMessage({
      defaultMessage: 'Your booking is confirmed.',
    });
    if (bookingCancelled) {
      pageHeader = this.props.intl.formatMessage({
        defaultMessage: 'Your booking has been cancelled.',
      });
    }

    return (
      <div className="ModifyBooking-Column left">
        <h1 className="header" data-testid="booking-cancelled-header">
          {pageHeader}
        </h1>
        <div className="bookingId">ID: {booking.id}</div>
        {bookingCancelled && (
          <p className="cancellation-date">
            <FormattedMessage
              id="CANCELLATION_DATE"
              defaultMessage="This booking was cancelled on {date}."
              values={{
                date: (
                  <FormattedDate
                    value={new Date()}
                    year="numeric"
                    month="short"
                    day="numeric"
                  />
                ),
              }}
            />
          </p>
        )}
        <div className="guestDetails">
          <div className="name">{booking.guestName}</div>
          <div>{`${booking.numberOfGuests} ${booking.guestsDescriptor}`}</div>
          <div data-testid="confirm-booking-details">
            <FormattedMessage
              defaultMessage="{date} at {time}"
              values={{
                date: booking.formattedDate,
                time: booking.formattedTime,
              }}
            />
          </div>
          {ticketLink}
        </div>
        {bookingCancelled && (
          <CancellationSummaryContainer>
            <p>
              <FormattedMessage
                id="CANCELLATION_EMAIL"
                defaultMessage="We sent confirmation of your cancellation to <strong>{email}</strong>"
                values={{
                  email,
                  strong: (chunks) => <strong>{chunks}</strong>,
                }}
              />
            </p>
            {!isFree && propsBooking.paid && (
              <CancellationRefundSection>
                <FormattedMessage
                  id="REFUND_SECTION"
                  defaultMessage="We''ve issued a refund of {formattedRefundAmount} to your card ending in {last4}. Please note that it can take 5-10 business days for the refund to post to your account."
                  values={{
                    formattedRefundAmount: finalFormattedRefundAmount(
                      selectedRefund,
                      cancellationPreview
                    ),
                    last4: propsBooking.card_last4,
                  }}
                />
              </CancellationRefundSection>
            )}
          </CancellationSummaryContainer>
        )}
        {this.renderTicketCapacityJobInProgressWarning()}
        <div className="actionButtons">
          {!bookingCancelled && (
            <BookingActionButton
              data-testid="reschedule-button"
              className={`brand-border brand-color rescheduleButton ${
                this.bookingReschedulable() ? null : 'disabled'
              }`}
              onClick={
                this.bookingReschedulable()
                  ? () => this.setState({ isRescheduling: true })
                  : () => {}
              }
              buttonColor={button_background_color}
            >
              <FormattedMessage
                id="RESCHEDULE_BUTTON_TEXT"
                defaultMessage="Reschedule"
              />
            </BookingActionButton>
          )}
          <BookingActionButton
            className="brand-border brand-color contactButton"
            onClick={this.openContactModal}
            buttonColor={button_background_color}
            data-testid="contact-us-button"
          >
            <FormattedMessage id="CONTACT_US" defaultMessage="Contact us" />
          </BookingActionButton>
        </div>
        {this.bookingCancellable() && (
          <div className="cancelbooking">
            <span>
              <FormattedMessage
                id="UNABLE_TO_ATTEND"
                defaultMessage="Unable to attend?"
              />
            </span>{' '}
            <span
              onClick={() => this.toggleCancelModal(true)}
              className="cancel-booking-link brand-color"
              data-testid="cancel-booking-link"
            >
              <FormattedMessage
                id="CANCEL_THIS_BOOKING"
                defaultMessage="Cancel this booking"
              />
            </span>
          </div>
        )}
      </div>
    );
  };

  render() {
    const { payments, header_data } = this.props;
    const { bookingProps, cancellationPreviewLoading, isRescheduling } =
      this.state;
    const { booking } = this.props;
    const availableDates = Object.keys(booking.availability_dates || {}).map(
      (dateStr) => parse(dateStr, 'yyyy-MM-dd', new Date())
    );

    const isoTime = Boolean(booking?.tour?.iso_time);

    return (
      <>
        <Dimmer
          loader={cancellationPreviewLoading}
          active={cancellationPreviewLoading}
          fixed
        />
        <div>
          {this.renderDesktopHeader()}
          <div className="ModifyBooking">
            {this.renderMobileCover()}
            {this.renderLeftPanel()}
            {this.renderCancelModal()}
            <div className="ModifyBooking-Column right">
              <BookingSummary
                {...bookingProps}
                payments={payments}
                link_color={header_data.brand_color}
              />
            </div>
          </div>
        </div>
        <GuestReschedule
          availableDates={availableDates}
          booking={bookingProps}
          isOpen={isRescheduling}
          onClose={this.onCancelReschedule}
          isoTime={isoTime}
          rescheduleStepRef={this.rescheduleStepRef}
        />
      </>
    );
  }
}

export default injectIntl(ModifyBooking);
